/**
 * Wishlist Manager - Moon Moon Agency
 *
 * Branchement de la wishlist Shopify (app Wishlist Power / Maestrooo) sur les boutons coeur du theme.
 *
 * API utilisee (cf. JavaScript API - Wishlist Power.pdf) :
 *   window.Maestrooo.wishlist.addProduct(handle, variant_id)
 *   window.Maestrooo.wishlist.removeProduct(handle, variant_id)
 *   window.Maestrooo.wishlist.containsProduct(handle) -> Promise<bool>
 *   window.Maestrooo.wishlist.getContent() -> Promise<{products: [...]}>
 *   Events : 'ooo-wishlist:product-added' / 'ooo-wishlist:product-removed'
 *
 * Granularite V1 : produit (handle seul, pas de variant_id).
 *
 * Page wishlist (template page.wishlist.json) : grid client-side via Shopify
 * Section Rendering API (1 fetch parallel par handle).
 */

/* Trace de chargement du fichier (raw console, non filtre par window.log) */
console.log('❤️ [WISHLIST] script loaded');

/*********************************************************************************************
 * ❤️ WISHLIST MANAGER
 */
const WishlistManager = {
	config: {
		selectors: {
			button:      '[data-product-wishlist]',
			badge:       '[data-wishlist-count]',
			grid:        '[data-wishlist-grid]',
			gridEmpty:   '[data-wishlist-empty]',
			gridLoading: '[data-wishlist-loading]',
			gridCountText: '[data-wishlist-count-text]',
			cardWrapper: '[data-wishlist-card-handle]',
		},
		classes: {
			active: 'is-wishlisted',
			hidden: 'mm-none',
		},
		/* section_id Shopify pour la Section Rendering API (sert juste a rendre la product card) */
		cardRenderSection: 'mm-product-card-render',
		/* Retry l'attente de window.Maestrooo.wishlist : l'app charge en async via app-embed */
		apiTimeoutMs:  2000,
		apiPollEveryMs: 100,
	},

	state: {
		ready: false,
		wishlistHandles: new Set(),
	},

	init() {
		const hasButton = document.querySelector(this.config.selectors.button);
		const hasGrid   = document.querySelector(this.config.selectors.grid);
		console.log('❤️ [WISHLIST] init', { hasButton: !!hasButton, hasGrid: !!hasGrid, maestrooo: !!window.Maestrooo });
		if (!hasButton && !hasGrid) return;

		this._waitForApi().then(() => this._onApiReady()).catch((err) => {
			console.warn('❤️ [WISHLIST] API Maestrooo non disponible — boutons inactifs', err.message);
		});
	},

	/* Attend window.Maestrooo.wishlist (max apiTimeoutMs). L'app-embed Wishlist Power
	 * charge son script en async, on ne peut pas garantir qu'il soit pret au DOMContentLoaded. */
	_waitForApi() {
		return new Promise((resolve, reject) => {
			if (window.Maestrooo?.wishlist) return resolve();
			let elapsed = 0;
			const interval = setInterval(() => {
				if (window.Maestrooo?.wishlist) {
					clearInterval(interval);
					return resolve();
				}
				elapsed += this.config.apiPollEveryMs;
				if (elapsed >= this.config.apiTimeoutMs) {
					clearInterval(interval);
					reject(new Error(`Timeout ${this.config.apiTimeoutMs}ms`));
				}
			}, this.config.apiPollEveryMs);
		});
	},

	_onApiReady() {
		this.state.ready = true;
		this._bindEvents();
		this._hydrateInitialState();
		const grid = document.querySelector(this.config.selectors.grid);
		if (grid) this._renderWishlistGrid(grid);
		console.log('❤️ [WISHLIST] Ready');
	},

	_bindEvents() {
		/* Delegation : un seul listener click pour tous les boutons (presents + a venir). */
		document.addEventListener('click', (e) => {
			const btn = e.target.closest(this.config.selectors.button);
			if (!btn) return;
			e.preventDefault();
			e.stopPropagation();
			console.log('❤️ [WISHLIST] click', { handle: btn.dataset.productHandle, wasActive: this.state.wishlistHandles.has(btn.dataset.productHandle) });
			this._toggle(btn);
		});

		/* Sync UI quand l'app dispatch les events globaux (cas multi-onglet, ou autre code) */
		document.addEventListener('ooo-wishlist:product-added',   (e) => this._onProductAdded(e.detail));
		document.addEventListener('ooo-wishlist:product-removed', (e) => this._onProductRemoved(e.detail));
	},

	/* 1 seul appel getContent() au load pour hydrater tous les boutons + badge. */
	async _hydrateInitialState() {
		try {
			const result = await window.Maestrooo.wishlist.getContent();
			const products = result?.products || [];
			this.state.wishlistHandles = new Set(products.map((p) => p.handle));
			this._refreshAllButtons();
			this._updateBadge(products.length);
		} catch (err) {
			window.log('warn', '❤️ [WISHLIST]', 'getContent failed', { error: err.message });
		}
	},

	async _toggle(btn) {
		const handle = btn.dataset.productHandle;
		if (!handle) return;
		const wasActive = this.state.wishlistHandles.has(handle);
		/* Optimiste : MAJ visuelle immediate sur le bouton clique */
		this._setButtonState(btn, !wasActive);
		try {
			if (wasActive) {
				await window.Maestrooo.wishlist.removeProduct(handle);
				this.state.wishlistHandles.delete(handle);
			} else {
				await window.Maestrooo.wishlist.addProduct(handle);
				this.state.wishlistHandles.add(handle);
			}
			/* Sync les autres boutons du meme handle (autre carte du meme produit visible) */
			this._refreshAllButtons();
		} catch (err) {
			/* Rollback visuel + state */
			this._setButtonState(btn, wasActive);
			window.log('error', '❤️ [WISHLIST]', 'Toggle failed', { handle, error: err.message });
		}
	},

	_onProductAdded(detail) {
		const handle = detail?.product?.handle;
		if (handle) this.state.wishlistHandles.add(handle);
		this._refreshAllButtons();
		if (typeof detail?.wishlist_products_count === 'number') {
			this._updateBadge(detail.wishlist_products_count);
		}
	},

	_onProductRemoved(detail) {
		const handle = detail?.product?.handle;
		if (handle) {
			this.state.wishlistHandles.delete(handle);
			this._removeFromGrid(handle);
		}
		this._refreshAllButtons();
		if (typeof detail?.wishlist_products_count === 'number') {
			this._updateBadge(detail.wishlist_products_count);
		}
	},

	_refreshAllButtons() {
		document.querySelectorAll(this.config.selectors.button).forEach((btn) => {
			const handle = btn.dataset.productHandle;
			if (!handle) return;
			this._setButtonState(btn, this.state.wishlistHandles.has(handle));
		});
	},

	_setButtonState(btn, isActive) {
		btn.setAttribute('aria-pressed', String(isActive));
		btn.classList.toggle(this.config.classes.active, isActive);
	},

	_updateBadge(count) {
		document.querySelectorAll(this.config.selectors.badge).forEach((badge) => {
			badge.textContent = String(count);
			badge.classList.toggle(this.config.classes.hidden, count === 0);
		});
		this._updateCountText(count);
	},

	/* Page wishlist : "X produit(s)" sous le titre.
	 * Les templates FR/EN sont injectes dans data-text-one / data-text-other
	 * (placeholder {{ count }} remplace cote JS pour avoir le bon nombre). */
	_updateCountText(count) {
		document.querySelectorAll(this.config.selectors.gridCountText).forEach((el) => {
			if (count === 0) {
				el.classList.add(this.config.classes.hidden);
				el.textContent = '';
				return;
			}
			const template = count === 1 ? el.dataset.textOne : el.dataset.textOther;
			if (!template) return;
			el.textContent = template.replace('{{ count }}', count);
			el.classList.remove(this.config.classes.hidden);
		});
	},

	/* ====================================================================== */
	/* GRID PAGE (template page.wishlist.json)                                 */
	/* ====================================================================== */

	async _renderWishlistGrid(grid) {
		const empty   = document.querySelector(this.config.selectors.gridEmpty);
		const loading = document.querySelector(this.config.selectors.gridLoading);
		loading?.classList.remove(this.config.classes.hidden);
		empty?.classList.add(this.config.classes.hidden);

		try {
			const result = await window.Maestrooo.wishlist.getContent();
			const products = result?.products || [];
			/* Maj du state + count text au passage (les 2 fetch getContent() tournent en parallele
			 * avec _hydrateInitialState — on ne sait pas lequel finit en premier, donc on
			 * met a jour le count ici aussi pour eviter un flash sur la page wishlist). */
			this.state.wishlistHandles = new Set(products.map((p) => p.handle));
			this._updateBadge(products.length);
			loading?.classList.add(this.config.classes.hidden);
			if (!products.length) {
				empty?.classList.remove(this.config.classes.hidden);
				return;
			}
			/* Fetch toutes les cards en parallele via la Section Rendering API */
			const cards = await Promise.all(products.map((p) => this._fetchCardHtml(p.handle)));
			cards.forEach((html, i) => {
				const handle = products[i].handle;
				const fragment = this._extractCardFragment(html, handle);
				if (fragment) grid.appendChild(fragment);
			});
			/* Apres injection : hydrate visuel des boutons coeur des cards fraichement
			 * injectees (aria-pressed=true + classe active) */
			this._refreshAllButtons();
			/* Re-init Splide sur les sliders des cards fraichement injectees
			 * (la card a un slider Splide si plusieurs variantes). Le manager a un guard
			 * element._splide qui evite le double-init des sliders deja inits. */
			if (window.SplideManager?.init) {
				window.SplideManager.init();
			}
		} catch (err) {
			loading?.classList.add(this.config.classes.hidden);
			empty?.classList.remove(this.config.classes.hidden);
			window.log('error', '❤️ [WISHLIST]', 'Grid render failed', { error: err.message });
		}
	},

	async _fetchCardHtml(handle) {
		try {
			/* Prefixe avec window.Shopify.routes.root pour respecter la locale courante
			 * (= '/' sur FR primaire, '/en/' sur EN, etc.). Sans ca, le HTML des cards
			 * de la page wishlist serait toujours en FR meme sur /en/pages/wishlist. */
			const root = window.Shopify?.routes?.root || '/';
			const url = `${root}products/${encodeURIComponent(handle)}?section_id=${this.config.cardRenderSection}`;
			const res = await fetch(url);
			if (!res.ok) return null;
			return await res.text();
		} catch { return null; }
	},

	/* Shopify Section Rendering API wrappe le contenu de la section dans un
	 * <div class="shopify-section">. On cible le wrapper wishlist (card + CTA "Voir le produit")
	 * injecte par MM-Product-Card-Render.liquid via son selector [data-wishlist-card-handle].
	 * Fallback sur [data-grid-card] au cas ou le wrapper serait absent (vieux cache CDN). */
	_extractCardFragment(html, handle) {
		if (!html) return null;
		const wrapper = document.createElement('div');
		wrapper.innerHTML = html.trim();
		const card = wrapper.querySelector('[data-wishlist-card-handle]') || wrapper.querySelector('[data-grid-card]');
		if (!card) return null;
		if (!card.hasAttribute('data-wishlist-card-handle')) {
			card.setAttribute('data-wishlist-card-handle', handle);
		}
		return card;
	},

	_removeFromGrid(handle) {
		const grid = document.querySelector(this.config.selectors.grid);
		if (!grid) return;
		const card = grid.querySelector(`[data-wishlist-card-handle="${CSS.escape(handle)}"]`);
		card?.remove();
		/* Re-affiche l'empty state si plus aucune card (les placeholders loading+empty
		 * restent dans le DOM mais caches, donc on compte specifiquement les cards). */
		if (!grid.querySelector(this.config.selectors.cardWrapper)) {
			document.querySelector(this.config.selectors.gridEmpty)?.classList.remove(this.config.classes.hidden);
		}
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => WishlistManager.init());
} else {
	WishlistManager.init();
}

window.WishlistManager = WishlistManager;
